import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';

const PLANS = [
  {
    name: 'Starter',
    price: 'Free',
    period: '',
    desc: 'Perfect for testing the platform and running your first campaign.',
    color: '#737373',
    bg: '#fff',
    border: '#E5E6EF',
    checkColor: '#4F46E5',
    checkBg: 'rgba(129,140,248,0.18)',
    features: [
      '1 connected platform',
      '3 campaigns / month',
      'Campaign Manager',
      'Basic analytics',
      'Email support',
    ],
    cta: 'Get started free',
    ctaLink: '/signup',
    ctaBg: '#F6F7FB',
    ctaColor: '#111',
    ctaBorder: '#CDCFE0',
  },
  {
    name: 'Pro',
    price: '€10',
    period: '/ month',
    desc: 'For active media buyers managing multiple campaigns daily.',
    color: '#fff',
    bg: '#0F0B2D',
    border: '#0F0B2D',
    checkColor: '#818CF8',
    checkBg: 'rgba(129,140,248,0.12)',
    popular: true,
    features: [
      'All platforms (TikTok, Meta, LinkedIn…)',
      'Unlimited campaigns',
      'AI content generation',
      'Automation rules',
      'Live performance metrics',
      'CSV export',
      'Priority support',
    ],
    cta: 'Start Pro',
    ctaLink: '/signup',
    ctaBg: '#818CF8',
    ctaColor: '#0F0B2D',
    annual: '€120 / year',
  },
  {
    name: 'Agency',
    price: '€15',
    period: '/ month',
    desc: 'For agencies managing multiple client accounts at scale.',
    color: '#737373',
    bg: '#fff',
    border: '#E5E6EF',
    checkColor: '#4F46E5',
    checkBg: 'rgba(129,140,248,0.18)',
    features: [
      'Everything in Pro',
      'Multi-account management',
      'Advanced automation',
      'Custom reporting',
      'Dedicated account manager',
      'API access',
      'SLA guarantee',
    ],
    cta: 'Contact us',
    ctaLink: '/signup',
    ctaBg: '#F6F7FB',
    ctaColor: '#111',
    ctaBorder: '#CDCFE0',
    annual: '€180 / year',
  },
];

const Pricing = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" ref={ref} style={{ background: '#F6F7FB', padding: '110px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        <div style={{ marginBottom: 64 }}>
          <div className="mono-label" style={{ color: '#737373', marginBottom: 22 }}>
            <span style={{ color: '#4F46E5' }}>/</span> Pricing
          </div>
          <h2 style={{ fontSize: 'clamp(28px,4vw,46px)', fontWeight: 700, color: '#111', letterSpacing: '-0.035em', marginBottom: 12, lineHeight: 1.1 }}>
            Simple, transparent{' '}
            <em className="serif-accent">pricing.</em>
          </h2>
          <p style={{ fontSize: 15, color: '#737373', maxWidth: 420 }}>
            Start for free. Upgrade when you need more power.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 20, alignItems: 'start' }}>
          {PLANS.map((plan, i) => (
            <div key={i} style={{
              padding: '32px 28px',
              borderRadius: 20,
              background: plan.bg,
              border: `1.5px solid ${plan.border}`,
              boxShadow: plan.popular
                ? '0 24px 64px rgba(0,0,0,0.22)'
                : 'none',
              opacity: visible ? 1 : 0,
              transform: visible ? (plan.popular ? 'scale(1.03)' : 'none') : 'translateY(28px)',
              transition: `all 0.6s ${i * 120}ms ease`,
              position: 'relative',
            }}>
              {plan.popular && (
                <div className="mono-label" style={{
                  position: 'absolute', top: -12, left: 28,
                  background: '#818CF8',
                  color: '#0F0B2D', fontSize: 9, fontWeight: 700,
                  padding: '4px 12px', borderRadius: 999,
                  whiteSpace: 'nowrap',
                }}>
                  Most popular
                </div>
              )}

              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: plan.popular ? 'rgba(255,255,255,0.6)' : plan.color, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                  <span style={{ fontSize: 40, fontWeight: 900, color: plan.popular ? '#fff' : '#111', letterSpacing: '-0.03em' }}>{plan.price}</span>
                  <span style={{ fontSize: 15, color: plan.popular ? 'rgba(255,255,255,0.5)' : '#9CA3AF', fontWeight: 500 }}>{plan.period}</span>
                </div>
                {plan.annual && (
                  <div style={{ fontSize: 12, color: plan.popular ? 'rgba(255,255,255,0.4)' : '#9CA3AF', marginBottom: 10 }}>or {plan.annual}</div>
                )}
                <p style={{ fontSize: 13.5, color: plan.popular ? 'rgba(255,255,255,0.6)' : '#6B7280', lineHeight: 1.6 }}>{plan.desc}</p>
              </div>

              <div style={{ height: 1, background: plan.popular ? 'rgba(255,255,255,0.12)' : '#E5E7EB', marginBottom: 24 }} />

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {plan.features.map((f, j) => (
                  <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: plan.popular ? 'rgba(255,255,255,0.8)' : '#374151' }}>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: plan.checkBg, border: plan.popular ? '1px solid rgba(255,255,255,0.2)' : `1px solid ${plan.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FaCheck style={{ fontSize: 8, color: plan.checkColor }} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <Link to={plan.ctaLink} style={{
                display: 'block', textAlign: 'center',
                padding: '13px 24px', borderRadius: 12,
                background: plan.ctaBg,
                border: plan.popular ? 'none' : `1px solid ${plan.ctaBorder || plan.border}`,
                color: plan.ctaColor, fontWeight: 700, fontSize: 14,
                textDecoration: 'none',
                boxShadow: plan.popular ? '0 4px 16px rgba(255,255,255,0.2)' : 'none',
                transition: 'all 0.2s',
              }}
                onMouseEnter={e => { e.target.style.opacity = '0.85'; e.target.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.target.style.opacity = '1'; e.target.style.transform = 'none'; }}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', marginTop: 32, fontSize: 13, color: '#9CA3AF' }}>
          No credit card required. Cancel anytime.
        </p>
      </div>
    </section>
  );
};

export default Pricing;
